<template>
  <div class="ocr-component-wrapper">
  <!-- Loading state while SDK initializes -->
  <div
    v-if="is_loading && current_step === 'instruction'"
    class="d-flex flex-column align-items-center justify-content-center py-5"
  >
    <div class="spinner-border text-primary mb-3" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted">{{ loading_message || 'Initializing...' }}</p>
  </div>

  <div
    v-else-if="current_step === 'instruction'"
    class="card ocr-instructions-card mt-2"
  >
    <div class="card-body shadow border-0">
      <h6 class="fw-bold text-center mb-3">Document Reader Instructions</h6>
      <div class="ocr-instructions-list">
        <div
          class="ocr-instruction-item"
          v-for="(item, idx) in instructionList"
          :key="idx"
        >
          <div class="ocr-instruction-icon">
            <i class="bi bi-check-circle"></i>
          </div>
          <div class="ocr-instruction-content">
            <span class="ocr-instruction-title">{{ item.title }}</span>
            <span class="ocr-instruction-desc">{{ item.desc }}</span>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center my-2">
        <button
          class="btn btn-bg-green text-xs btn-sm"
          @click="initialize_document_reader"
          :disabled="is_loading"
        >
          <span
            v-if="is_loading"
            class="spinner-border spinner-border-sm me-1"
            role="status"
          ></span>
          Start Scanning
        </button>
      </div>
    </div>
  </div>

  <!-- CRITICAL: Only render document-reader AFTER camera permission is granted -->
  <!-- This ensures orientation is locked before SDK component mounts -->
  <div
    class="document-streaming-reader"
    v-else-if="current_step === 'reader' && sdkComponentsReady && orientationReady && cameraPermissionGranted"
  >
    <document-reader
      :settings="documentReaderSettings"
      copyright="false"
    ></document-reader>
  </div>

  <!-- Loading state while preparing camera/orientation -->
  <!-- Uses document-streaming-reader class to inherit fullscreen styles -->
  <div
    v-else-if="current_step === 'reader'"
    class="document-streaming-reader d-flex flex-column align-items-center justify-content-center"
  >
    <div class="spinner-border text-light mb-3" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-light">
      {{ loading_message || 'Preparing camera...' }}
    </p>
  </div>
</div>

</template>
<script>
  import { validateToken } from "@/utils/auth";
import {
  defineComponents,
  DocumentReaderService,
} from "@regulaforensics/vp-frontend-document-components";
import Swal from "sweetalert2";

const WORKER_PATH =
  process.env.VUE_APP_REGULA_WORKER_PATH ||
  process.env.REGULA_WORKER_PATH ||
  null;

const DEFAULT_STREAM_PARAM = {
  cameraMode: "environment",
  resolution: {
    width: 1920,
    height: 1080,
  },
};

const DEFAULT_RECOGNIZER_PROCESS_PARAM = {
  processParam: {
    scenario: "MRZOrLocate",
    multipageProcessing: true,
    timeout: 90000,
    timeoutFromFirstDetect: 90000,
    timeoutFromFirstDocType: 90000,
    returnCroppedBarcode: true,
    checkRequiredTextFields: false,
    imageQualityCheck: false,
    minDPI: 75,
    returnUncroppedImage: false,
    depersonalizeLog: true,
    matchTextFieldMask: false,
  },
};

const DEFAULT_IMAGE_PROCESS_PARAM = {
  processParam: {
    scenario: "Locate",
    alreadyCropped: false,
    returnUncroppedImage: false,
    doublePageSpread: false,
    checkRequiredTextFields: false,
    perspectiveAngle: 60,
    forceReadMrzBeforeLocate: false,
    imageQualityCheck: false,
  },
};

const DEFAULT_PROCESS_PARAMS = {
  doOCR: true,
  doBarcode: true,
  doRFID: false,
  doImageQA: true,
  doAuthenticity: false,
  doVDS: false,
  returnPackageForReprocess: false,
  timeout: 90000,
  processAllPages: false,
  checkRequiredTextFields: false,
};

const DEFAULT_COMPONENT_SETTINGS = {
  startScreen: false,
  finishScreen: false,
  internalScenario: "Locate",
  captureMode: "auto",
  multipageProcessing: true,
  cameraMode: DEFAULT_STREAM_PARAM.cameraMode,
  videoCaptureMotionControl: false,
  changeCameraButton: true,
  finalizeOnTimeout: false,
  mirrorButton: true,
  cameraFrameLandscapeAspectRatio: 1.58,
  // Default portrait ratio - will be overridden on mobile to force landscape hint-container
  cameraFramePortraitAspectRatio: 0.63,
  torchButton: false,
};

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergeObjects(base, override) {
  if (!override) {
    return { ...base };
  }

  const merged = { ...base };

  Object.keys(override).forEach((key) => {
    const baseValue = merged[key];
    const overrideValue = override[key];

    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      merged[key] = mergeObjects(baseValue, overrideValue);
    } else {
      merged[key] = overrideValue;
    }
  });

  return merged;
}

function mergeProcessParam(base, override) {
  if (!override) {
    return mergeObjects(base, null);
  }

  const merged = mergeObjects(base, null);
  merged.processParam = mergeObjects(
    base.processParam || {},
    override.processParam || {}
  );
  return merged;
}

const buildComponentSettings = (overrides) =>
  mergeObjects(DEFAULT_COMPONENT_SETTINGS, overrides);

const createCaptureMetrics = (overrides = {}) => ({
  initiatedAt: null,
  finishedAt: null,
  durationMs: null,
  status: null,
  ...overrides,
});

let regulaInitialization = null;

async function ensureRegulaDocumentSDK() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (!window.RegulaDocumentSDK) {
    window.RegulaDocumentSDK = new DocumentReaderService();

    if (WORKER_PATH) {
      window.RegulaDocumentSDK.workerPath = WORKER_PATH;
    }

    window.RegulaDocumentSDK.streamParam = mergeObjects(DEFAULT_STREAM_PARAM);
    window.RegulaDocumentSDK.recognizerProcessParam = mergeProcessParam(
      DEFAULT_RECOGNIZER_PROCESS_PARAM
    );
    window.RegulaDocumentSDK.imageProcessParam = mergeProcessParam(
      DEFAULT_IMAGE_PROCESS_PARAM
    );
    window.RegulaDocumentSDK.processParams = mergeObjects(
      DEFAULT_PROCESS_PARAMS
    );
  }

  if (!regulaInitialization) {
    const licenseKey = process.env.BASE64_KEY || process.env.VUE_APP_BASE64_KEY;

    if (!licenseKey) {
      return Promise.reject(
        new Error("Regula SDK license key is missing. Please contact support.")
      );
    }

    regulaInitialization = defineComponents()
      .then(() => {
        return window.RegulaDocumentSDK.initialize({ license: licenseKey });
      })
      .catch((error) => {
        regulaInitialization = null;
        throw error;
      });
  }

  return regulaInitialization;
}

export default {
  name: "OCRStreamComponent",

  props: {
    readerConfig: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      current_step: "instruction",
      loading_message: "",
      error_message: null,
      show_timeout_alert: false,
      timeout_countdown: 0,
      is_loading: false,
      sdkComponentsReady: false,
      cameraPermissionGranted: false,
      torchAvailable: false,
      torchEnabled: false,
      permissionsFreshlyGranted: false,
      orientationReady: false,
      viewportDimensions: null,
      scan_results: null,
      document_status: null,
      document_list: [],
      document_images: [],
      processing_status: {
        optical: null,
        rfid: null,
        overall: null,
      },
      processingTimeoutId: null,
      maxProcessingTime: 90000,
      captureMetrics: createCaptureMetrics(),

      instructionList: [
        {
          title: "Optimal Lighting:",
          desc: "Ensure adequate lighting for clear visibility. Avoid overly bright or dim conditions that may affect document processing quality.",
        },
        {
          title: "Eliminate Reflections:",
          desc: "Position your device to avoid glare and reflections. Disable flash to prevent interference with data extraction accuracy.",
        },
        {
          title: "Sharp Focus:",
          desc: "Hold your device steady and ensure the document appears crisp and clear without any blurred sections.",
        },
        {
          title: "Proper Alignment:",
          desc: "Keep the document straight with minimal tilt. The angle should not exceed 10 degrees in any direction.",
        },
        {
          title: "Strong Contrast:",
          desc: "Place your document on a contrasting background. Light documents work best on dark surfaces and vice versa.",
        },
        {
          title: "High Resolution:",
          desc: "Use a camera with Full HD resolution (1920×1080) minimum and enable autofocus for optimal recognition quality.",
        },
        {
          title: "Unobstructed View:",
          desc: "Keep fingers and other objects away from the document to ensure all information is clearly visible.",
        },
        {
          title: "Flat Surface:",
          desc: "For passports and booklets, ensure pages lie flat with all text and details clearly visible without distortion.",
        },
      ],
    };
  },

  computed: {
    documentReaderSettings() {
      const overrides = this.readerConfig?.settings;
      return buildComponentSettings(overrides);
    },
  },

  methods: {
    async initialize_document_reader() {
      this.loading_message = "Requesting camera access...";
      this.error_message = null;
      this.show_timeout_alert = false;
      this.is_loading = true;
      this.resetCaptureMetrics();
      this.markCaptureStarted();

      if (this.processingTimeoutId) {
        clearTimeout(this.processingTimeoutId);
        this.processingTimeoutId = null;
      }

      const isAndroid = /Android/i.test(navigator.userAgent);
      const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isChrome =
        /Chrome/i.test(navigator.userAgent) &&
        !/Edg/i.test(navigator.userAgent);
      const isAndroidChrome = isAndroid && isChrome;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Step 1: Request camera permission and setup orientation BEFORE anything else
      let permissionsAlreadyGranted = false;
      if (isMobile) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          permissionsAlreadyGranted = permissionStatus.state === 'granted';
        } catch (e) {
          console.warn('Could not query permission state:', e);
        }

        if (permissionsAlreadyGranted) {
          await this.enterFullScreenAndLockOrientationEarly();
        } else {
          this.forceFullScreenStyles();
        }
      }

      // Request camera permission
      const hasPermission = await this.requestCameraPermissions();
      if (!hasPermission) {
        this.is_loading = false;
        if (isMobile) {
          await this.exitFullScreenAndUnlockOrientation();
        }
        return;
      }

      // Mark permission as granted
      this.cameraPermissionGranted = true;
      console.log('[OCR] Camera permission granted');

      // Step 2: Setup orientation properly after permission is granted
      if (isMobile && !permissionsAlreadyGranted) {
        // [CRITICAL] After fresh permission grant, setup orientation before SDK initialization
        console.log('[OCR] Permissions freshly granted - establishing orientation lock');
        
        this.loading_message = "Setting up camera orientation...";
        
        // Wait for permission dialog to fully dismiss
        await new Promise((resolve) => setTimeout(resolve, 200));
        
        // Re-enter fullscreen and lock orientation
        await this.enterFullScreenAndLockOrientationEarly();
        
        // Wait for orientation lock to take effect
        await new Promise((resolve) => setTimeout(resolve, 400));
        
        // Additional wait for Android Chrome
        if (isAndroidChrome) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
        
        // Additional wait for iOS
        if (isiOS) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
        
        // CRITICAL: Verify we're actually in landscape before proceeding
        if (!this.isLandscapeOrientation()) {
          console.warn('[OCR] Not in landscape after permission grant, forcing orientation...');
          await this.tryForceOrientation();
          await this.waitForLandscapeOrientation(2000);
        }
        
        console.log(`[OCR] Post-permission orientation check: ${window.innerWidth}x${window.innerHeight}, landscape: ${this.isLandscapeOrientation()}`);
      }

      // Step 3: Initialize SDK after orientation is confirmed
      try {
        this.loading_message = "Loading document scanner...";
        console.log('[OCR] Initializing Regula SDK...');
        await ensureRegulaDocumentSDK();
        this.sdkComponentsReady = true;
        console.log('[OCR] SDK initialized successfully');
        
        // Don't configure SDK here - will configure after final orientation check
        if (!isMobile) {
          this.configureRegulaSDK();
        }
      } catch (error) {
        this.is_loading = false;
        let errorMessage = "Failed to initialize document reader.";

        if (error?.message?.includes("license")) {
          errorMessage =
            "Document scanner license error. Please contact support.";
        } else if (
          error?.message?.includes("WebAssembly") ||
          error?.message?.includes("wasm")
        ) {
          errorMessage =
            "Your browser does not support the document scanner. Please try Chrome or update your browser.";
        } else if (
          error?.message?.includes("network") ||
          error?.message?.includes("fetch")
        ) {
          errorMessage =
            "Network error loading document scanner. Please check your connection and try again.";
        } else if (isAndroid) {
          errorMessage = `Document scanner failed to load on Android. Error: ${
            error?.message || "Unknown error"
          }. Please try Chrome browser.`;
        } else {
          errorMessage =
            error?.message ||
            "Failed to initialize document reader. Please try again later.";
        }

        this.error_message = errorMessage;

        if (isAndroid) {
          Swal.fire({
            icon: "error",
            title: "Scanner Initialization Failed",
            html: `<div style="text-align: left;">
                            ${errorMessage}<br><br>
                            <strong>Troubleshooting:</strong><br>
                            • Use Chrome browser<br>
                            • Clear browser cache<br>
                            • Check internet connection<br>
                            • Allow all permissions
                        </div>`,
            confirmButtonText: "Retry",
            allowOutsideClick: false,
          }).then(() => {
            window.location.reload();
          });
        }
        return;
      }

      // Step 4: Final orientation verification before rendering document-reader component
      if (isMobile) {
        this.loading_message = "Preparing camera view...";
        
        const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
        const isCurrentlyLandscape = this.isLandscapeOrientation();
        
        console.log(`[OCR] Final pre-render check - Fullscreen: ${isCurrentlyFullscreen}, Landscape: ${isCurrentlyLandscape}, Viewport: ${window.innerWidth}x${window.innerHeight}`);
        
        // [CRITICAL] If not in landscape, aggressively try to fix it
        if (!isCurrentlyLandscape) {
          console.warn('[OCR] Not in landscape orientation - attempting to fix before component render');
          
          await this.enterFullScreenAndLockOrientation();
          await this.tryForceOrientation();
          await this.waitForLandscapeOrientation(3000);
          
          if (!this.isLandscapeOrientation()) {
            console.error('[OCR] CRITICAL: Could not achieve landscape orientation. SDK may capture partial images.');
          }
        } else if (!isCurrentlyFullscreen) {
          await this.enterFullScreenAndLockOrientation();
        }

        // Wait for any pending orientation/fullscreen changes to settle
        await new Promise((resolve) => setTimeout(resolve, 300));

        if (isAndroidChrome) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
        if (isiOS) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // Capture final viewport dimensions
        this.viewportDimensions = {
          width: window.innerWidth,
          height: window.innerHeight,
          screenWidth: screen.width,
          screenHeight: screen.height,
          orientation: screen.orientation?.type || 'unknown'
        };
        console.log('[OCR] Final viewport dimensions before component render:', this.viewportDimensions);

        // Configure SDK with landscape dimensions
        this.configureRegulaSDKForMobile();
      }

      // Step 5: Mark everything as ready - this will trigger document-reader component to render
      this.orientationReady = true;
      this.loading_message = "Starting camera...";
      
      console.log('[OCR] All prerequisites ready - switching to reader view');
      console.log(`[OCR] Final state - SDK: ${this.sdkComponentsReady}, Permission: ${this.cameraPermissionGranted}, Orientation: ${this.orientationReady}`);

      // Switch to reader view - this will render the document-reader component
      this.current_step = "reader";

      // Set timeout guard
      const timeoutDuration = isAndroidChrome
        ? this.maxProcessingTime + 30000
        : this.maxProcessingTime;
      this.processingTimeoutId = setTimeout(() => {
        if (this.is_loading) {
          this.markCaptureFinished("timeout");
          this.is_loading = false;
          Swal.fire({
            icon: "error",
            title: "Processing Timeout",
            html:
              '<div style="text-align: left;">' +
              "Document processing took too long.<br><br>" +
              "<strong>For multipage documents:</strong><br>" +
              "• Scan both front and back clearly<br>" +
              "• Ensure good lighting<br>" +
              "• Hold document steady<br>" +
              "• Complete scan within 15 seconds" +
              "</div>",
            confirmButtonText: "Retry",
            allowOutsideClick: false,
          }).then(() => {
            this.resetScan();
          });
        }
      }, timeoutDuration);

      // Emit scan-started event to parent
      this.$emit("scan-started");

      // Step 6: Wait for Vue to render the document-reader component, then attach listeners
      await this.$nextTick();
      
      // Additional delay to ensure DOM and shadow DOM are fully ready
      if (isMobile) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      
      console.log('[OCR] Attaching document reader listeners and starting camera');
      this.attachDocumentReaderListeners();
      this.is_loading = false;
    },

    configureRegulaSDK() {
      if (typeof window === "undefined" || !window.RegulaDocumentSDK) {
        return;
      }

      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const componentSettings = buildComponentSettings(
        this.readerConfig?.settings
      );
      const streamParam = mergeObjects(
        DEFAULT_STREAM_PARAM,
        this.readerConfig?.streamParam
      );

      streamParam.cameraMode = componentSettings.cameraMode;

      if (isMobile) {
        const isAndroid = /Android/i.test(navigator.userAgent);
        // Use lower resolution for Android compatibility (some devices don't support 1080p)
        streamParam.resolution = isAndroid
          ? { width: 1280, height: 720 }
          : { width: 1920, height: 1080 };

        // Enable torch button for Android (many docs fail in low light)
        if (isAndroid) {
          componentSettings.torchButton = true;
        }

        // Force hint-container to stay landscape on mobile by using the same aspect ratio
        componentSettings.cameraFramePortraitAspectRatio =
          componentSettings.cameraFrameLandscapeAspectRatio;
      } else if (componentSettings.resolution) {
        streamParam.resolution = mergeObjects(
          DEFAULT_STREAM_PARAM.resolution,
          componentSettings.resolution
        );
      }

      const recognizerParam = mergeProcessParam(
        DEFAULT_RECOGNIZER_PROCESS_PARAM,
        this.readerConfig?.recognizerProcessParam
      );
      const scenario =
        componentSettings.internalScenario ||
        recognizerParam.processParam?.scenario ||
        DEFAULT_RECOGNIZER_PROCESS_PARAM.processParam.scenario;

      recognizerParam.processParam.scenario = scenario;
      if (typeof componentSettings.multipageProcessing === "boolean") {
        recognizerParam.processParam.multipageProcessing =
          componentSettings.multipageProcessing;
      }

      const imageParam = mergeProcessParam(
        DEFAULT_IMAGE_PROCESS_PARAM,
        this.readerConfig?.imageProcessParam
      );
      imageParam.processParam.scenario = scenario;

      const processParams = mergeObjects(
        DEFAULT_PROCESS_PARAMS,
        this.readerConfig?.processParams
      );
      if (typeof componentSettings.multipageProcessing === "boolean") {
        processParams.processAllPages = componentSettings.multipageProcessing;
      }

      window.RegulaDocumentSDK.streamParam = streamParam;
      window.RegulaDocumentSDK.recognizerProcessParam = recognizerParam;
      window.RegulaDocumentSDK.imageProcessParam = imageParam;
      window.RegulaDocumentSDK.processParams = processParams;

      this.activeReaderSettings = componentSettings;
    },

    /**
     * [CRITICAL FIX] Configure SDK specifically for mobile with landscape-aware settings.
     * Called AFTER orientation lock is confirmed to ensure correct dimensions are captured.
     */
    configureRegulaSDKForMobile() {
      if (typeof window === "undefined" || !window.RegulaDocumentSDK) {
        return;
      }

      const isAndroid = /Android/i.test(navigator.userAgent);
      const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      
      // Get current viewport dimensions (should be landscape by now)
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isLandscape = viewportWidth > viewportHeight;
      
      console.log(`[OCR] Configuring SDK for mobile. Viewport: ${viewportWidth}x${viewportHeight}, Landscape: ${isLandscape}`);

      const componentSettings = buildComponentSettings(this.readerConfig?.settings);
      
      // [CRITICAL FIX] Force stream params to use landscape dimensions
      const streamParam = {
        cameraMode: "environment",
        resolution: isAndroid
          ? { width: 1280, height: 720 }
          : { width: 1920, height: 1080 },
      };

      // [CRITICAL FIX] Disable the hint-container frame to use full viewport for capture
      // The aspect ratio controls the frame size, but we need FULL viewport capture
      // Setting both to same value AND disabling frame border forces full capture area
      componentSettings.cameraFrameLandscapeAspectRatio = 0; // 0 = use full viewport
      componentSettings.cameraFramePortraitAspectRatio = 0; // 0 = use full viewport
      componentSettings.cameraFrameDefaultRatio = 0; // Force full viewport capture
      
      // [CRITICAL FIX] Force video to use full viewport without cropping
      componentSettings.cameraFrameBorderWidth = 0; // No border/frame
      componentSettings.cameraFrameShapeType = 0; // Rectangle (not rounded corners)
      
      // Disable any viewport cropping or letterboxing
      componentSettings.cameraFrameOffsetWidth = 0;
      
      // Enable torch for Android (helps in low light conditions)
      if (isAndroid) {
        componentSettings.torchButton = true;
      }

      // [CRITICAL FIX] For iOS, use specific constraints
      if (isiOS) {
        streamParam.resolution = { width: 1920, height: 1080 };
      }

      const recognizerParam = mergeProcessParam(
        DEFAULT_RECOGNIZER_PROCESS_PARAM,
        this.readerConfig?.recognizerProcessParam
      );
      const scenario =
        componentSettings.internalScenario ||
        recognizerParam.processParam?.scenario ||
        DEFAULT_RECOGNIZER_PROCESS_PARAM.processParam.scenario;

      recognizerParam.processParam.scenario = scenario;
      if (typeof componentSettings.multipageProcessing === "boolean") {
        recognizerParam.processParam.multipageProcessing =
          componentSettings.multipageProcessing;
      }

      const imageParam = mergeProcessParam(
        DEFAULT_IMAGE_PROCESS_PARAM,
        this.readerConfig?.imageProcessParam
      );
      imageParam.processParam.scenario = scenario;

      const processParams = mergeObjects(
        DEFAULT_PROCESS_PARAMS,
        this.readerConfig?.processParams
      );
      if (typeof componentSettings.multipageProcessing === "boolean") {
        processParams.processAllPages = componentSettings.multipageProcessing;
      }

      // Apply all configurations to SDK
      window.RegulaDocumentSDK.streamParam = streamParam;
      window.RegulaDocumentSDK.recognizerProcessParam = recognizerParam;
      window.RegulaDocumentSDK.imageProcessParam = imageParam;
      window.RegulaDocumentSDK.processParams = processParams;

      this.activeReaderSettings = componentSettings;
      
      console.log('[OCR] SDK configured with stream params:', streamParam);
      console.log('[OCR] SDK configured with aspect ratios - landscape:', componentSettings.cameraFrameLandscapeAspectRatio, 'portrait:', componentSettings.cameraFramePortraitAspectRatio);
    },

    applyDocumentReaderSettings() {
      if (!this.documentReaderComponent) {
        return;
      }

      const overrides = this.readerConfig?.settings;
      const mergedSettings =
        this.activeReaderSettings || buildComponentSettings(overrides);

      // Apply settings object
      this.documentReaderComponent.settings = mergedSettings;

      // Allow SDK defaults for styling by clearing any prior inline overrides
      this.documentReaderComponent.style.removeProperty(
        "--regula-primary-color"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-success-color"
      );
      this.documentReaderComponent.style.removeProperty("--regula-error-color");
      this.documentReaderComponent.style.removeProperty(
        "--regula-warning-color"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-frame-border-color"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-frame-border-width"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-frame-background"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-border-color"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-overlay-background"
      );
      this.documentReaderComponent.style.removeProperty(
        "--regula-hint-background"
      );
      this.documentReaderComponent.removeAttribute("data-hide-frame");

      this.activeReaderSettings = mergedSettings;
    },

    resetCaptureMetrics() {
      this.captureMetrics = createCaptureMetrics();
    },

    markCaptureStarted() {
      this.captureMetrics = createCaptureMetrics({
        initiatedAt: Date.now(),
        status: "in-progress",
      });
    },

    markCaptureFinished(statusLabel = "completed") {
      const finishedAt = Date.now();
      const initiatedAt = this.captureMetrics?.initiatedAt ?? finishedAt;
      const durationMs = Math.max(0, finishedAt - initiatedAt);

      this.captureMetrics = createCaptureMetrics({
        initiatedAt,
        finishedAt,
        durationMs,
        status: statusLabel,
      });
    },

    setCaptureStatus(statusLabel) {
      const initiatedAt = this.captureMetrics?.initiatedAt ?? null;
      const finishedAt = this.captureMetrics?.finishedAt ?? null;
      const durationMs = this.captureMetrics?.durationMs ?? null;

      this.captureMetrics = createCaptureMetrics({
        initiatedAt,
        finishedAt,
        durationMs,
        status: statusLabel,
      });
    },

    getCaptureMetricsSnapshot() {
      if (!this.captureMetrics || this.captureMetrics.durationMs === null) {
        return null;
      }

      const { initiatedAt, finishedAt, durationMs, status } =
        this.captureMetrics;
      return {
        durationMs,
        status,
        startedAtMs: initiatedAt,
        finishedAtMs: finishedAt,
      };
    },

    attachDocumentReaderListeners() {
      this.documentReaderComponent = this.$el.querySelector("document-reader");
      this.cameraSnapshotComponent = this.$el.querySelector("camera-snapshot");

      if (this.documentReaderComponent) {
        // Remove any existing listeners first
        this.documentReaderComponent.removeEventListener(
          "document-reader",
          this.handleDocumentReader
        );

        // Remove error listener if exists
        if (this.handleCameraError) {
          this.documentReaderComponent.removeEventListener(
            "camera-error",
            this.handleCameraError
          );
        }

        // Add the main event handler
        this.documentReaderComponent.addEventListener(
          "document-reader",
          this.handleDocumentReader
        );

        // Add camera error handler for better debugging on Android Chrome
        this.handleCameraError = (event) => {
          console.error("Camera error event:", event);
          const isAndroidChrome =
            /Android/i.test(navigator.userAgent) &&
            /Chrome/i.test(navigator.userAgent) &&
            !/Edg/i.test(navigator.userAgent);

          if (isAndroidChrome) {
            this.handleProcessingError({
              errorMessage:
                "<strong>Camera error on Android Chrome.</strong><br><br>" +
                "The camera could not be initialized.<br><br>" +
                "<strong>Please try:</strong><br>" +
                "• Closing other apps using the camera<br>" +
                "• Refreshing the page<br>" +
                "• Clearing browser cache<br>" +
                "• Restarting your device",
            });
          }
        };

        this.documentReaderComponent.addEventListener(
          "camera-error",
          this.handleCameraError
        );
      }

      if (this.cameraSnapshotComponent) {
        this.cameraSnapshotComponent.removeEventListener(
          "camera-snapshot",
          this.handleCameraSnapshot
        );
        this.cameraSnapshotComponent.addEventListener(
          "camera-snapshot",
          this.handleCameraSnapshot
        );
      }

      this.applyDocumentReaderSettings();
      this.configureRegulaSDK();

      // Start camera (permissions already granted in initialize_document_reader)
      this.startSDKCamera();
    },

    async requestCameraPermissions() {
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isChrome =
        /Chrome/i.test(navigator.userAgent) &&
        !/Edg/i.test(navigator.userAgent);
      const isAndroidChrome = isAndroid && isChrome;

      try {
        // Request camera permission with constraints that work well on Android Chrome
        const constraints = {
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: isAndroidChrome ? 1280 : 1920 },
            height: { ideal: isAndroidChrome ? 720 : 1080 },
          },
          audio: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Check if torch is available on this device
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && isAndroid) {
          try {
            const capabilities = videoTrack.getCapabilities
              ? videoTrack.getCapabilities()
              : {};
            this.torchAvailable = capabilities.torch === true;
          } catch (e) {
            console.warn("Could not check torch capability:", e);
            this.torchAvailable = false;
          }
        }

        // Stop all tracks properly
        stream.getTracks().forEach((track) => {
          track.stop();
        });

        // Android Chrome needs extra time to fully release the camera hardware
        // This is critical - without this delay, the SDK cannot acquire the camera
        if (isAndroidChrome) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        } else if (isAndroid) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // [ASSUMED] Mark that permissions were freshly granted (not cached)
        // This helps us know if we need to reconfigure orientation after SDK init
        this.permissionsFreshlyGranted = true;

        return true;
      } catch (error) {
        console.error("Camera permission error:", error);

        let errorMessage =
          "Camera access is required to scan documents. Please allow camera permissions and try again.";

        if (error.name === "NotAllowedError") {
          errorMessage =
            "Camera permission denied. Please enable camera access in your browser settings and try again.";
        } else if (error.name === "NotFoundError") {
          errorMessage =
            "No camera found on this device. Please ensure your device has a camera.";
        } else if (
          error.name === "NotReadableError" ||
          error.name === "AbortError"
        ) {
          errorMessage =
            "Camera is in use by another application. Please close other apps using the camera and try again.";
        } else if (error.name === "OverconstrainedError") {
          // Try again with basic constraints
          try {
            const basicStream = await navigator.mediaDevices.getUserMedia({
              video: true,
            });
            basicStream.getTracks().forEach((track) => track.stop());
            if (isAndroid) {
              await new Promise((resolve) => setTimeout(resolve, 800));
            }
            this.permissionsFreshlyGranted = true;
            return true;
          } catch (retryError) {
            errorMessage = "Camera configuration error. Please try again.";
          }
        }

        this.handleProcessingError({ errorMessage });
        return false;
      }
    },

    async startSDKCamera() {
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isChrome =
        /Chrome/i.test(navigator.userAgent) &&
        !/Edg/i.test(navigator.userAgent);
      const isAndroidChrome = isAndroid && isChrome;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Give devices time for camera hardware - varies by platform
      if (isAndroidChrome) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else if (isAndroid) {
        await new Promise((resolve) => setTimeout(resolve, 600));
      } else if (isiOS) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      }

      await this.$nextTick();

      if (!this.documentReaderComponent) {
        console.error("Document reader component not found");
        return;
      }

      if (typeof this.documentReaderComponent.startCamera !== "function") {
        console.error(
          "startCamera method not available on document reader component"
        );
        return;
      }

      // [CRITICAL FIX] Verify orientation one more time before starting camera
      if (isMobile && !this.isLandscapeOrientation()) {
        console.warn('[OCR] Not in landscape before camera start, reconfiguring...');
        await this.waitForLandscapeOrientation(1500);
        // Reconfigure SDK with current dimensions
        this.configureRegulaSDKForMobile();
        this.applyDocumentReaderSettings();
      }

      const maxRetries = isAndroidChrome ? 3 : 2;
      const retryDelays = isAndroidChrome ? [1500, 2000, 2500] : [1000, 1500];

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          await this.documentReaderComponent.startCamera();
          if (isMobile) {
            await this.$nextTick();
            await new Promise((resolve) => setTimeout(resolve, 200));

            // [CRITICAL FIX] Force reconfigure after camera starts
            // Some SDKs capture dimensions on camera start
            this.applyDocumentReaderSettings();
            
            // [CRITICAL FIX] Verify video element dimensions
            await this.verifyVideoDimensions();
          }

          this.permissionsFreshlyGranted = false;

          return; // Success - exit the retry loop
        } catch (error) {
          console.warn(`Camera start attempt ${attempt + 1} failed:`, error);

          if (attempt < maxRetries - 1) {
            // Wait before retrying
            const delay = retryDelays[attempt] || 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            // Final attempt failed
            console.error("All camera start attempts failed");

            // Show user-friendly error for Android Chrome
            if (isAndroidChrome) {
              this.handleProcessingError({
                errorMessage:
                  "<strong>Camera failed to start on Android Chrome.</strong><br><br>" +
                  "<strong>Please try:</strong><br>" +
                  "• Close other apps using the camera<br>" +
                  "• Refresh the page and try again<br>" +
                  "• Clear Chrome cache and data<br>" +
                  "• Restart your device<br><br>" +
                  "<strong>If the issue persists, try using a different browser.</strong>",
              });
            }
          }
        }
      }
    },

    /**
     * [CRITICAL FIX] Verify video element dimensions match expected landscape orientation.
     * Logs warning if dimensions seem incorrect and forces full viewport capture.
     */
    async verifyVideoDimensions() {
      try {
        const videoElement =
          this.documentReaderComponent?.shadowRoot?.querySelector("video") ||
          this.$el.querySelector("video");

        if (!videoElement) {
          console.warn('[OCR] Could not find video element for dimension verification');
          return;
        }

        // Wait for video to have dimensions
        await new Promise((resolve) => setTimeout(resolve, 300));

        const videoWidth = videoElement.videoWidth || videoElement.clientWidth;
        const videoHeight = videoElement.videoHeight || videoElement.clientHeight;
        
        console.log(`[OCR] Video dimensions: ${videoWidth}x${videoHeight}`);
        console.log(`[OCR] Viewport dimensions: ${window.innerWidth}x${window.innerHeight}`);
        
        // [CRITICAL FIX] Force video to use full viewport dimensions
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        
        // Force hint-container to full viewport as well
        const hintContainer =
          this.documentReaderComponent?.shadowRoot?.querySelector('.hint-container') ||
          this.$el.querySelector('.hint-container');
        
        if (hintContainer) {
          hintContainer.style.width = '100%';
          hintContainer.style.height = '100%';
          hintContainer.style.maxWidth = '100%';
          hintContainer.style.maxHeight = '100%';
          hintContainer.style.left = '0';
          hintContainer.style.top = '0';
          hintContainer.style.transform = 'none';
          console.log('[OCR] Forced hint-container to full viewport');
        }
        
        // Force camera-view container to full viewport
        const cameraView =
          this.documentReaderComponent?.shadowRoot?.querySelector('.camera-view') ||
          this.documentReaderComponent?.shadowRoot?.querySelector('.video-container') ||
          this.$el.querySelector('.camera-view');
        
        if (cameraView) {
          cameraView.style.width = '100%';
          cameraView.style.height = '100%';
          console.log('[OCR] Forced camera-view to full viewport');
        }
        
        // Check if video is landscape
        if (videoWidth > 0 && videoHeight > 0 && videoWidth < videoHeight) {
          console.warn('[OCR] WARNING: Video appears to be in portrait orientation!');
          console.warn('[OCR] This may cause partial image capture. Consider restarting scan.');
          
          // Try to reconfigure SDK one more time
          this.configureRegulaSDKForMobile();
        } else {
          console.log('[OCR] Video orientation verified as landscape');
        }
      } catch (e) {
        console.warn('[OCR] Could not verify video dimensions:', e);
      }
    },

    /**
     * Enable torch/flashlight on Android devices for better document capture in low light.
     * This accesses the camera's torch capability through the ImageCapture API.
     */
    async enableTorchForAndroid() {
      try {
        // Try to access the active video track from the SDK's stream
        const videoElement =
          this.documentReaderComponent?.shadowRoot?.querySelector("video") ||
          this.$el.querySelector("video");

        if (!videoElement || !videoElement.srcObject) {
          console.warn("No video element or stream found for torch control");
          return;
        }

        const stream = videoElement.srcObject;
        const videoTrack = stream.getVideoTracks()[0];

        if (!videoTrack) {
          console.warn("No video track found for torch control");
          return;
        }

        // Check if torch is supported
        const capabilities = videoTrack.getCapabilities
          ? videoTrack.getCapabilities()
          : {};

        if (!capabilities.torch) {
          this.torchAvailable = false;
          return;
        }

        // Enable torch
        await videoTrack.applyConstraints({
          advanced: [{ torch: true }],
        });

        this.torchEnabled = true;
      } catch (error) {
        console.warn("Could not enable torch:", error);
        this.torchEnabled = false;
      }
    },

    /**
     * Disable torch/flashlight
     */
    async disableTorch() {
      try {
        const videoElement =
          this.documentReaderComponent?.shadowRoot?.querySelector("video") ||
          this.$el.querySelector("video");

        if (!videoElement || !videoElement.srcObject) {
          return;
        }

        const stream = videoElement.srcObject;
        const videoTrack = stream.getVideoTracks()[0];

        if (videoTrack) {
          await videoTrack.applyConstraints({
            advanced: [{ torch: false }],
          });
          this.torchEnabled = false;
        }
      } catch (error) {
        console.warn("Could not disable torch:", error);
      }
    },

    /**
     * Toggle torch on/off
     */
    async toggleTorch() {
      if (this.torchEnabled) {
        await this.disableTorch();
      } else {
        await this.enableTorchForAndroid();
      }
    },

    handleDocumentReader(event) {
      const action = event.detail ? event.detail.action : null;
      if (action === "CLOSE" || action === "CAMERA_PROCESS_CLOSED") {
        if (this.processingTimeoutId) {
          clearTimeout(this.processingTimeoutId);
          this.processingTimeoutId = null;
        }
        this.is_loading = false;
        this.$emit("close");
        window.location.reload();
        return;
      }

      if (event.detail.action === "PROCESS_FINISHED") {
        // Clear the timeout guard
        if (this.processingTimeoutId) {
          clearTimeout(this.processingTimeoutId);
          this.processingTimeoutId = null;
        }

        this.markCaptureFinished();
        this.is_loading = false;
        const { data } = event.detail;

        // STATUS 0 means cancelled/no action - This is an error!
        if (data.status === 0) {
          Swal.close();
          this.setCaptureStatus("failure");
          this.handleProcessingError({
            ...data,
            errorMessage:
              "<strong>Document scanner failed to start.</strong><br><br>" +
              "<strong>Possible causes:</strong><br>" +
              "• Camera permissions not granted<br>" +
              "• Browser doesn't support camera access<br>" +
              "• SDK initialization error<br><br>" +
              "<strong>Please allow camera access and try again.</strong>",
          });
          return;
        }

        if (data.status === 1) {
          // Show loading Swal immediately on successful scan (status 1 = Success from SDK)
          Swal.fire({
            title: "Processing Document",
            html: "Please wait while we verify your document...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          this.setCaptureStatus("success");
          const response = data.response || {};

          this.processDocumentResponse(response);
          this.document_list =
            response?.lowLvlResponse?.ContainerList?.List || [];

          this.emitDocumentProcessed({
            documentList: this.document_list,
            transactionId:
              response?.TransactionInfo?.TransactionId || data.transactionId,
            rawResponse: response,
          });
        } else {
          Swal.close();
          this.setCaptureStatus("failure");
          const response = data.response || {};

          let errorMessage = "Failed to process document. Please try again.";

          if (response.status) {
            const failedChecks = [];
            const detailsOptical = response.status.detailsOptical || {};

            if (detailsOptical.text === 2)
              failedChecks.push("❌ Text extraction failed");
            if (detailsOptical.mrz === 2)
              failedChecks.push("❌ MRZ reading failed");
            if (detailsOptical.docType === 2)
              failedChecks.push("❌ Document type not recognized");
            if (detailsOptical.optical === 2 || response.status.optical === 2)
              failedChecks.push("❌ Optical/OCR check failed");
            if (detailsOptical.imageQA === 2)
              failedChecks.push("❌ Image quality check failed");

            if (failedChecks.length > 0) {
              errorMessage =
                "<strong>Processing failed - Cannot read document.</strong><br><br>" +
                "<strong>Failed Checks:</strong><br>" +
                failedChecks.join("<br>") +
                "<br><br>" +
                "<strong>Possible causes:</strong><br>" +
                "• Poor focus/blur<br>" +
                "• Bad lighting/glare<br>" +
                "• Document not flat<br>" +
                "• Movement during scan<br><br>" +
                "<strong>Please retry with better conditions.</strong>";
            }
          }

          this.handleProcessingError({ ...data, errorMessage });
          return;
        }
      }
    },

    handleCameraSnapshot(event) {
      if (event.detail.action === "PROCESS_FINISHED") {
        // Clear the timeout guard
        if (this.processingTimeoutId) {
          clearTimeout(this.processingTimeoutId);
          this.processingTimeoutId = null;
        }

        this.markCaptureFinished();
        this.is_loading = false;
        const { data } = event.detail;

        if (data.status === 1) {
          this.setCaptureStatus("success");
          const response = data.response || {};

          this.processDocumentResponse(response);
          this.document_list =
            response?.lowLvlResponse?.ContainerList?.List || [];

          this.emitDocumentProcessed({
            documentList: this.document_list,
            transactionId:
              response?.TransactionInfo?.TransactionId || data.transactionId,
            rawResponse: response,
          });
        } else {
          this.setCaptureStatus("failure");
          this.handleProcessingError(data);
        }
      }
    },

    handleProcessingError(data) {
      this.setCaptureStatus("failure");
      this.error_message =
        data?.errorMessage || "Failed to process document. Please try again.";

      Swal.fire({
        icon: "error",
        title: "❌ Cannot Process Document",
        html: '<div style="text-align: left;">' + this.error_message + "</div>",
        confirmButtonText: "Retry Scan",
        showCancelButton: false,
        allowOutsideClick: false,
        footer:
          "<strong>You must retry - cannot continue with failed scan</strong>",
      }).then(() => {
        this.resetScan();
      });

      // Emit error - parent should NOT proceed
      this.emitDocumentProcessed({
        error: this.error_message,
        failed: true,
        processingFailed: true,
        rawResponse: data,
      });
    },

    processDocumentResponse(response) {
      this.document_status = response.status;
      this.processing_status = {
        optical: this.getStatusText(response.status?.optical),
        rfid: this.getStatusText(response.status?.rfid),
        overall: this.getStatusText(response.status?.overallStatus),
      };

      if (response.images && response.images.fieldList) {
        this.document_images = response.images.fieldList.map((field) => {
          return {
            name: field.fieldName,
            type: field.fieldType,
            images: field.valueList.map((value) => ({
              source: value.source,
              pageIndex: value.pageIndex,
              imageData: value.value,
            })),
          };
        });
      }

      this.scan_results = {
        transactionInfo: response.TransactionInfo,
        detailsOptical: response.status?.detailsOptical,
        detailsRFID: response.status?.detailsRFID,
        rawResponse: response.rawResponse,
      };

      this.$emit("scan-complete", {
        status: this.processing_status,
        images: this.document_images,
        rawData: this.scan_results,
      });
    },

    getStatusText(statusCode) {
      const statusMap = {
        0: "Unknown",
        1: "Good",
        2: "Error",
        3: "Warning",
      };
      return statusMap[statusCode] || "Unknown";
    },

    getStatusClass(status) {
      const classMap = {
        Good: "text-success",
        Error: "text-danger",
        Warning: "text-warning",
        Unknown: "text-muted",
      };
      return classMap[status] || "text-muted";
    },

    emitDocumentProcessed(payload) {
      const metrics = this.getCaptureMetricsSnapshot();
      const enrichedPayload = metrics ? { ...payload, metrics } : payload;

      // Exit fullscreen and restore orientation when done processing
      this.exitFullScreenAndUnlockOrientation();

      this.$emit("document-processed", enrichedPayload);
    },

    async saveDocumentData() {
      if (!this.scan_results) return;

      try {
        this.is_loading = true;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        this.$toast.success("Document data saved successfully");
      } catch (error) {
        this.error_message = error.message || "Failed to save document data";
        this.$toast.error(this.error_message);
      } finally {
        this.is_loading = false;
      }
    },

    /**
     * Check if the current viewport is in landscape orientation.
     * Uses both window dimensions and screen.orientation API.
     */
    isLandscapeOrientation() {
      // Check window dimensions first (most reliable for viewport)
      const isViewportLandscape = window.innerWidth > window.innerHeight;
      
      // Also check screen.orientation API if available
      if (screen.orientation && screen.orientation.type) {
        const orientationType = screen.orientation.type;
        const isScreenLandscape = orientationType.includes('landscape');
        // Return true if either indicates landscape (prefer viewport check)
        return isViewportLandscape || isScreenLandscape;
      }
      
      return isViewportLandscape;
    },

    /**
     * Wait for the viewport to be in landscape orientation.
     * This is critical to ensure SDK captures correct dimensions.
     * @param {number} maxWaitMs - Maximum time to wait in milliseconds
     * @returns {Promise<boolean>} - True if landscape achieved, false if timeout
     */
    async waitForLandscapeOrientation(maxWaitMs = 3000) {
      const startTime = Date.now();
      const checkInterval = 50; // Check more frequently for faster response

      // If already in landscape, return immediately
      if (this.isLandscapeOrientation()) {
        console.log('[OCR] Already in landscape orientation');
        return true;
      }

      console.log('[OCR] Waiting for landscape orientation...');

      return new Promise((resolve) => {
        const checkOrientation = () => {
          const width = window.innerWidth;
          const height = window.innerHeight;
          const isLandscape = width > height;

          if (isLandscape) {
            console.log(`[OCR] Landscape achieved: ${width}x${height}`);
            resolve(true);
            return;
          }

          const elapsed = Date.now() - startTime;
          if (elapsed >= maxWaitMs) {
            console.warn(`[OCR] Landscape orientation timeout after ${maxWaitMs}ms. Current: ${width}x${height}`);
            // Try to force orientation one more time as last resort
            this.tryForceOrientation();
            // Still resolve true to allow SDK to try - some devices don't support orientation lock
            resolve(true);
            return;
          }

          setTimeout(checkOrientation, checkInterval);
        };

        checkOrientation();
      });
    },

    /**
     * [NEW METHOD] Try to force orientation lock as last resort.
     */
    async tryForceOrientation() {
      if (screen.orientation && screen.orientation.lock) {
        try {
          await screen.orientation.lock("landscape-primary");
          console.log('[OCR] Forced landscape-primary orientation');
        } catch (e) {
          try {
            await screen.orientation.lock("landscape");
            console.log('[OCR] Forced landscape orientation');
          } catch (e2) {
            console.warn('[OCR] Could not force orientation:', e2);
          }
        }
      }
    },

    /**
     * Request fullscreen and orientation lock EARLY during user gesture.
     * This is called IMMEDIATELY when user clicks "Start Scanning" to capture
     * the user gesture before it expires due to async operations.
     * Now WAITS for orientation to help ensure it's applied before permission dialog.
     */
    async enterFullScreenAndLockOrientationEarly() {
      // Apply fullscreen CSS styles immediately
      this.forceFullScreenStyles();

      const isAndroid = /Android/i.test(navigator.userAgent);

      try {
        // Request native fullscreen - MUST happen during user gesture
        const element = document.documentElement;
        let fullscreenPromise;
        
        if (element.requestFullscreen) {
          fullscreenPromise = element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          fullscreenPromise = element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          fullscreenPromise = element.msRequestFullscreen();
        }

        if (fullscreenPromise) {
          await fullscreenPromise;
        }

        // Wait for fullscreen to settle
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Lock orientation - now we AWAIT this to ensure it's applied
        if (screen.orientation && screen.orientation.lock) {
          try {
            await screen.orientation.lock("landscape-primary");
            console.log('[OCR] Early orientation lock: landscape-primary');
          } catch (err) {
            try {
              await screen.orientation.lock("landscape");
              console.log('[OCR] Early orientation lock: landscape');
            } catch (err2) {
              console.warn('[OCR] Early orientation lock failed:', err2);
            }
          }
        }

        // Wait briefly for orientation to apply
        if (isAndroid) {
          await new Promise((resolve) => setTimeout(resolve, 150));
        } else {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        console.log(`[OCR] Early setup complete - Viewport: ${window.innerWidth}x${window.innerHeight}`);
        
      } catch (err) {
        console.warn('[OCR] Early fullscreen request failed:', err);
        // Still continue - CSS fullscreen styles are applied
      }
    },

    async enterFullScreenAndLockOrientation() {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isAndroid = /Android/i.test(navigator.userAgent);

      // Always apply fullscreen styles on mobile
      if (isMobile) {
        this.forceFullScreenStyles();
      }

      // Try to enter native fullscreen and lock orientation (may fail on some devices)
      if (!isMobile) return;

      console.log('[OCR] Entering fullscreen and locking orientation...');

      try {
        const element = document.documentElement;
        
        // Enter fullscreen first
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }

        // Wait a bit for fullscreen to apply
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Now lock orientation - try multiple variants for maximum compatibility
        if (screen.orientation && screen.orientation.lock) {
          try {
            // Try landscape-primary first (most specific)
            await screen.orientation.lock("landscape-primary");
            console.log('[OCR] Orientation locked to landscape-primary');
          } catch (lockErr) {
            try {
              // Fallback to generic landscape
              await screen.orientation.lock("landscape");
              console.log('[OCR] Orientation locked to landscape');
            } catch (lockErr2) {
              console.warn('[OCR] Could not lock orientation:', lockErr2);
              // On Android, try again after a delay - sometimes works second time
              if (isAndroid) {
                await new Promise((resolve) => setTimeout(resolve, 200));
                try {
                  await screen.orientation.lock("landscape");
                  console.log('[OCR] Orientation locked on retry');
                } catch (e) {
                  // Give up on orientation lock
                }
              }
            }
          }
        }

        // Wait for orientation change to actually apply to viewport
        await this.waitForLandscapeOrientation(2000);

      } catch (err) {
        console.warn("[OCR] Could not enter full screen or lock orientation:", err);
        // Still try to force orientation even if fullscreen failed
        await this.tryForceOrientation();
        await this.waitForLandscapeOrientation(1500);
      }
    },

    async exitFullScreenAndUnlockOrientation() {
      // Disable torch when exiting
      if (this.torchEnabled) {
        await this.disableTorch();
      }

      // Remove fullscreen class and styles first
      document.body.classList.remove("ocr-fullscreen-active");
      document.documentElement.classList.remove("ocr-fullscreen-active");
      this.removeFullScreenStyles();

      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      if (!isMobile) return;

      try {
        if (document.fullscreenElement) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
          }
        }

        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }
      } catch (err) {
        console.warn("Could not exit full screen or unlock orientation:", err);
      }
    },

    forceFullScreenStyles() {
      const styleId = "ocr-fullscreen-styles";
      if (document.getElementById(styleId)) return;

      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
                body.ocr-fullscreen-active,
                html.ocr-fullscreen-active {
                    overflow: hidden !important;
                    position: fixed !important;
                    width: 100% !important;
                    height: 100% !important;
                }
                .ocr-component-wrapper {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100vw !important;
                    height: 100vh !important;
                    z-index: 2147483647 !important;
                    background: #000 !important;
                }
                .document-streaming-reader {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100vw !important;
                    height: 100vh !important;
                    z-index: 2147483647 !important;
                    background: #000 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    display: block !important;
                }
                document-reader {
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                    display: block !important;
                    background: #000 !important;
                }
                
                /* Ensure Swal appears above the fullscreen SDK overlay */
                .swal2-container {
                    z-index: 2147483647 !important;
                }
                .swal2-popup {
                    z-index: 2147483647 !important;
                }
            `;
      document.head.appendChild(style);
      document.body.classList.add("ocr-fullscreen-active");
      document.documentElement.classList.add("ocr-fullscreen-active");
    },

    removeFullScreenStyles() {
      const style = document.getElementById("ocr-fullscreen-styles");
      if (style) {
        style.remove();
      }
      document.body.classList.remove("ocr-fullscreen-active");
      document.documentElement.classList.remove("ocr-fullscreen-active");

      // Reset any inline styles that might have been set
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.position = "";

      // Scroll back to top so user can see the instruction card
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    },

    resetScan() {
      // Clear timeout
      if (this.processingTimeoutId) {
        clearTimeout(this.processingTimeoutId);
        this.processingTimeoutId = null;
      }

      this.resetCaptureMetrics();
      this.markCaptureStarted();
      this.scan_results = null;
      this.document_status = null;
      this.document_images = [];
      this.processing_status = {
        optical: null,
        rfid: null,
        overall: null,
      };
      this.error_message = null;

      if (this.documentReaderComponent && this.documentReaderComponent.reset) {
        this.documentReaderComponent.reset();
      }

      // Return to reader to scan again
      this.current_step = "reader";
    },
  },

  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }

    ensureRegulaDocumentSDK()
      .then(() => {
        this.configureRegulaSDK();
        if (this.current_step !== "instruction") {
          this.$nextTick(() => {
            this.attachDocumentReaderListeners();
          });
        }
      })
      .catch((error) => {
        this.error_message =
          error?.message ||
          "Failed to initialize document reader. Please try again later.";
      });
  },

  beforeUnmount() {
    this.exitFullScreenAndUnlockOrientation();
    // Clear timeout on destroy
    if (this.processingTimeoutId) {
      clearTimeout(this.processingTimeoutId);
    }

    if (this.documentReaderComponent) {
      this.documentReaderComponent.removeEventListener(
        "document-reader",
        this.handleDocumentReader
      );
      if (this.handleCameraError) {
        this.documentReaderComponent.removeEventListener(
          "camera-error",
          this.handleCameraError
        );
      }
    }
    if (this.cameraSnapshotComponent) {
      this.cameraSnapshotComponent.removeEventListener(
        "camera-snapshot",
        this.handleCameraSnapshot
      );
    }
  },
};
</script>

<style scoped>
/* OCR Component wrapper - no overflow */
.ocr-component-wrapper {
  width: 100%;
  overflow: hidden;
}

.ocr-instructions-card {
  overflow: hidden;
}

.ocr-instructions-card .card-body {
  overflow: hidden;
}

.ocr-instructions-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0 20px;
  overflow: hidden;
}

.document-streaming-reader {
  position: relative;
  margin-top: 20px;
  width: 100%;
  min-height: 300px;
  overflow: hidden;
}

/* Styles for SDK's built-in multipage UI customization */
document-reader {
  --regula-primary-color: var(--primary-color, #2f80ed);
  --regula-success-color: var(--success-color, #27ae60);
}

/* [CRITICAL FIX] Force hint-container and video to use full viewport in landscape */
/* This prevents the SDK from only capturing half the viewport */
::v-deep document-reader,
::ng-deep document-reader {
  width: 100% !important;
  height: 100% !important;
}

/* Force the hint-container (the frame showing where to place document) to full viewport */
::v-deep .hint-container,
::ng-deep .hint-container {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  left: 0 !important;
  top: 0 !important;
  transform: none !important;
}

/* Force video element to use full viewport */
::v-deep video,
::ng-deep video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

/* Force the camera view container to full size */
::v-deep .camera-view,
::ng-deep .camera-view,
::v-deep .video-container,
::ng-deep .video-container {
  width: 100% !important;
  height: 100% !important;
}

/* Remove any frame borders or overlays that might restrict capture area */
::v-deep .camera-frame,
::ng-deep .camera-frame {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
}

/* Custom styling for SDK multipage controls */
::ng-deep document-reader .multipage-controls {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 8px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.error-message {
  font-size: 12px;
  color: var(--bs-danger);
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid var(--bs-danger);
  border-radius: 4px;
  width: 100%;
  text-align: center;
}

.ocr-instructions-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0 20px;
}

.ocr-instruction-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-radius: 8px;
}

.ocr-instruction-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ocr-instruction-icon i {
  font-size: 24px;
  color: var(--primary-color);
}

.ocr-instruction-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ocr-instruction-title {
  font-weight: 600;
  font-size: 13px;
}

.ocr-instruction-desc {
  font-size: 12px;
  font-weight: 400;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Timeout alert animation */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.timeout-alert-message {
  animation: pulse 1s ease-in-out infinite;
}
</style>
